import React, { useState } from 'react';
import { apiCall } from '../services/api';
import ScriptGenerator from './ScriptGenerator';
import RecommendationsPanel from './RecommendationsPanel';
import ActionItems from './ActionItems';
import ContentGapsVisualization from './ContentGapsVisualization';
import RankingFactorsChart from './RankingFactorsChart';
import TitleVariations from './TitleVariations';
import ExportPanel from './ExportPanel';
import '../styles/AdvancedAnalysis.css';

export default function AdvancedAnalysis({ user, onCreditsUpdate }) {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [userCredits, setUserCredits] = useState(user?.credits || 0);

  // Comment Mining State (Phase 1.2)
  const [miningVideoInput, setMiningVideoInput] = useState('');
  const [miningLoading, setMiningLoading] = useState(false);
  const [commentData, setCommentData] = useState(null);
  const [miningError, setMiningError] = useState('');

  const handleMineComments = async (vidToMine) => {
    const targetId = vidToMine || miningVideoInput || results?.videoAnalysis?.topVideos?.[0]?.videoId;
    if (!targetId) {
      setMiningError('Please enter a YouTube video URL or ID');
      return;
    }

    setMiningLoading(true);
    setMiningError('');
    try {
      const res = await apiCall('/analysis/mine-comments', 'POST', { videoId: targetId });
      if (res.success) {
        setCommentData(res.data);
        if (res.credits && onCreditsUpdate) {
          setUserCredits(res.credits.remaining);
          onCreditsUpdate(res.credits.remaining);
        }
      }
    } catch (err) {
      setMiningError(err.message || 'Comment mining failed');
    } finally {
      setMiningLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!keyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      console.log('🚀 Advanced Analysis for:', keyword);

      const response = await apiCall('/analysis/advanced-analysis', 'POST', {
        keyword: keyword.trim(),
        generateScript: true
      });

      console.log('✅ Results:', response);

      if (response.credits) {
        setUserCredits(response.credits.remaining);
        if (onCreditsUpdate) {
          onCreditsUpdate(response.credits.remaining);
        }
        console.log(`💳 Credits: ${response.credits.used} deducted, ${response.credits.remaining} remaining`);
      }

      // FIX: Extract data from response
      const analysisData = response.data || response;
      setResults(analysisData);
      setActiveTab('overview');
    } catch (err) {
      console.error('❌ Error:', err);

      if (err.message === 'INSUFFICIENT_CREDITS' && err.details) {
        const { required, available } = err.details;
        setError(`❌ Insufficient credits. Need: ${required}, Available: ${available}. Buy more at dashboard.`);
      } else {
        setError(err.message || 'Analysis failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="advanced-analysis-container">
      <div className="analysis-header">
        <h1>🎯 YouTube Content Strategy Analyzer</h1>
        <p>Data-Driven YouTube Keyword Analysis & Audience Intelligence</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Enter YouTube keyword (e.g., 'web development for beginners')"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={loading}
              className="main-search-input"
            />
            <button type="submit" disabled={loading} className="analyze-btn">
              {loading ? '⏳ Analyzing...' : '🔍 Analyze'}
            </button>
          </div>

          <div className="credit-info-bar">
            <span className="credit-cost">💳 20 credits per analysis</span>
            <span className="credit-available">Your balance: <strong>{userCredits}</strong> credits</span>
          </div>
        </form>

        {error && <div className="error-alert">❌ {error}</div>}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Analyzing top videos and generating content strategy...</p>
          <div className="progress-steps">
            <div className="step active">Fetching videos</div>
            <div className="step">Analyzing content gaps</div>
            <div className="step">Mining comments</div>
            <div className="step">Generating script</div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="results-container">
          <div className="results-header">
            <h2>📊 Analysis Results for "{keyword}"</h2>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-navigation">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button
              className={`tab ${activeTab === 'gaps' ? 'active' : ''}`}
              onClick={() => setActiveTab('gaps')}
            >
              🔍 Content Gaps
            </button>
            <button
              className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              💬 Comment Mining
            </button>
            <button
              className={`tab ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommendations')}
            >
              💡 Recommendations
            </button>
            <button
              className={`tab ${activeTab === 'script' ? 'active' : ''}`}
              onClick={() => setActiveTab('script')}
            >
              ✍️ Script
            </button>
            <button
              className={`tab ${activeTab === 'ranking' ? 'active' : ''}`}
              onClick={() => setActiveTab('ranking')}
            >
              📈 Ranking Factors
            </button>
            <button
              className={`tab ${activeTab === 'titles' ? 'active' : ''}`}
              onClick={() => setActiveTab('titles')}
            >
              📝 Titles
            </button>
            <button
              className={`tab ${activeTab === 'actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('actions')}
            >
              ✅ Action Items
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-tab">
                <h3>📊 Analysis Overview</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-icon">📹</div>
                    <div className="metric-value">{results?.videoAnalysis?.totalAnalyzed || 0}</div>
                    <div className="metric-label">Videos Analyzed</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">🎯</div>
                    <div className="metric-value">{results?.contentGaps?.missingSubtopics?.length || 0}</div>
                    <div className="metric-label">Content Gaps Found</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">❓</div>
                    <div className="metric-value">{results?.contentGaps?.unansweredQuestions?.length || 0}</div>
                    <div className="metric-label">Questions Identified</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">💡</div>
                    <div className="metric-value">{results?.recommendations?.length || 0}</div>
                    <div className="metric-label">Recommendations</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'gaps' && <ContentGapsVisualization data={results} />}
            
            {activeTab === 'comments' && (
              <div className="comment-mining-tab" style={{ padding: '1rem' }}>
                <h3>💬 Real Comment Mining & Audience Sentiment</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Extract real viewer questions, sentiment distribution, and unaddressed video topic requests from any YouTube video.
                </p>
                
                <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '1.5rem' }}>
                  <input
                    type="text"
                    placeholder="Enter YouTube Video URL or Video ID (10 credits)"
                    value={miningVideoInput}
                    onChange={(e) => setMiningVideoInput(e.target.value)}
                    style={{ flex: 1, padding: '0.7rem 1rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                  />
                  <button 
                    onClick={() => handleMineComments(miningVideoInput)} 
                    disabled={miningLoading}
                    style={{ padding: '0.7rem 1.5rem', background: 'linear-gradient(90deg, #ec4899, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {miningLoading ? '⏳ Mining...' : '💬 Mine Comments'}
                  </button>
                </div>

                {miningError && <div className="error-alert" style={{ marginBottom: '1rem' }}>❌ {miningError}</div>}

                {commentData && (
                  <div>
                    {/* Sentiment Overview Bar */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.2rem', borderRadius: '12px', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                        <span>Audience Sentiment: <strong style={{ color: commentData.sentimentAnalysis.overallSentiment === 'Positive' ? '#10b981' : '#f59e0b' }}>{commentData.sentimentAnalysis.overallSentiment}</strong></span>
                        <span>Total Mined: <strong>{commentData.totalCommentsMined} comments</strong> {commentData.isLiveComments && ' (Live YouTube API)'}</span>
                      </div>
                      <div style={{ display: 'flex', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${commentData.sentimentAnalysis.positivePct}%`, background: '#10b981' }} title={`Positive: ${commentData.sentimentAnalysis.positivePct}%`}></div>
                        <div style={{ width: `${commentData.sentimentAnalysis.neutralPct}%`, background: '#64748b' }} title={`Neutral: ${commentData.sentimentAnalysis.neutralPct}%`}></div>
                        <div style={{ width: `${commentData.sentimentAnalysis.negativePct}%`, background: '#ef4444' }} title={`Negative: ${commentData.sentimentAnalysis.negativePct}%`}></div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        <span style={{ color: '#34d399' }}>🟢 Positive: {commentData.sentimentAnalysis.positivePct}%</span>
                        <span style={{ color: '#94a3b8' }}>⚪ Neutral: {commentData.sentimentAnalysis.neutralPct}%</span>
                        <span style={{ color: '#f87171' }}>🔴 Negative: {commentData.sentimentAnalysis.negativePct}%</span>
                      </div>
                    </div>

                    {/* Extracted Viewer Questions */}
                    {commentData.questionsExtracted && commentData.questionsExtracted.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#c084fc', marginBottom: '0.5rem' }}>❓ Top Extracted Viewer Questions ({commentData.questionsExtracted.length})</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                          {commentData.questionsExtracted.map((q, idx) => (
                            <div key={idx} style={{ background: 'rgba(0,0,0,0.2)', padding: '0.8rem 1rem', borderRadius: '8px', borderLeft: '3px solid #8b5cf6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong style={{ color: '#e2e8f0' }}>"{q.question}"</strong>
                                {q.author && <span style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.2rem' }}>Asked by {q.author}</span>}
                              </div>
                              <span style={{ background: 'rgba(139,92,246,0.2)', color: '#c084fc', padding: '0.2rem 0.5rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 }}>
                                👍 {q.likes} likes
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recommendations' && <RecommendationsPanel data={results} />}
            {activeTab === 'script' && <ScriptGenerator data={results} />}
            {activeTab === 'ranking' && <RankingFactorsChart data={results} />}
            {activeTab === 'titles' && <TitleVariations data={results} />}
            {activeTab === 'actions' && <ActionItems data={results} />}
          </div>

          {/* Export Panel */}
          <ExportPanel data={results} keyword={keyword} />
        </div>
      )}
    </div>
  );
}
